import { useState, useEffect, useRef, useMemo } from 'react';

interface UseTypewriterOptions {
  examples: string[];
  typingSpeed?: number;
  pauseDuration?: number;
  enabled?: boolean;
}

export const useTypewriter = ({
  examples,
  typingSpeed = 50,
  pauseDuration = 2000,
  enabled = true,
}: UseTypewriterOptions) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  // Memoize examples to prevent unnecessary re-renders
  const stableExamples = useMemo(() => examples, [JSON.stringify(examples)]);
  
  // Use refs to track current state without triggering re-renders
  const charIndexRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText('');
      return;
    }
    
    const currentExample = stableExamples[placeholderIndex];
    charIndexRef.current = 0;
    setIsTyping(true);
    setDisplayedText('');
    
    // Clear any existing timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    intervalRef.current = setInterval(() => {
      if (charIndexRef.current < currentExample.length) {
        charIndexRef.current++;
        setDisplayedText(currentExample.slice(0, charIndexRef.current));
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setIsTyping(false);
        // Wait before moving to next example
        timeoutRef.current = setTimeout(() => {
          setPlaceholderIndex(prev => (prev + 1) % stableExamples.length);
        }, pauseDuration);
      }
    }, typingSpeed);
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, placeholderIndex, stableExamples, typingSpeed, pauseDuration]);

  return { displayedText, isTyping };
};
